import { computed, ref } from 'vue';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import messagingApi from '@/common/api/messaging.api';
import { buildAidboxQueryString, extractAidboxQueryData } from '@/composables/useAidboxQuery';

/**
 * The message inbox (#5331).
 *
 * Reads through the `message-inbox` AidboxQuery rather than fetching participant rows and then a
 * thread each. Three reasons, all from #5343's access model:
 *
 * 1. It would be an N+1, which CLAUDE.md prohibits.
 * 2. `messaging-thread-read` requires the thread id as `resource/id` or `_id` and will not
 *    satisfy a comma-separated multi-`_id` search, so "fetch 20 threads at once" isn't available.
 * 3. `_include` on the participant search would smuggle thread data past the patient-access check
 *    that policy puts on thread reads, because the policy binds to the request's resource/type.
 *
 * The query takes no identity parameter — it scopes itself with `{{user.practitioner.id}}` — so
 * there is nothing a caller can pass to read someone else's inbox.
 *
 * The state is module-scope on purpose, following `useNavBar`. Two components read this inbox at
 * once — the app-bar button in `DefaultLayout` and `MessagesPage` — and per-instance refs gave them
 * independent copies, so marking a thread read on the page left the app-bar dot lit until the next
 * page load. One shared source means the badge and the list cannot disagree.
 */
const threads = ref([]);
const loading = ref(false);

/**
 * The SEARCHED/FILTERED inbox (#5402), deliberately its own state rather than a narrowing of
 * `threads` above.
 *
 * `threads` is not just the Messages list. `unreadCount` is derived from it and feeds the app-bar
 * badge in `views/layouts/DefaultLayout.vue` as well as the chip on the page, `markThreadRead`
 * looks the thread up in it and no-ops when it is absent, and `useMessageThread` reads it too. So
 * making `fetchInbox` filtered — the obvious-looking single-list version of this feature — would
 * have two silent consequences: a search for "intake" would drop the badge to whatever matched,
 * making the reader believe they had no other unread conversations; and opening a conversation from
 * a filtered list would find nothing to update and quietly skip the read receipt.
 *
 * Two lists instead. The unfiltered read stays exactly as it was and remains the badge's only
 * source; the filtered read is a second, disposable result set the page renders while a filter is
 * on. `markThreadRead` updates a thread in either or both, so the two can never disagree about what
 * has been read.
 */
const filteredThreads = ref([]);
const filteredTotal = ref(0);
const searching = ref(false);

const unreadCount = computed(() => threads.value.filter((thread) => thread.unread).length);

/**
 * Drop the inbox on sign-out and on account switch (#5331).
 *
 * Module scope means this state outlives the components that render it, and signing out is pure
 * SPA state clearing — no reload — so without this the next user in the same tab inherits the
 * previous one's threads. That matters precisely because `fetchInbox` below degrades to the last
 * good list on failure: with no reset, a failed first fetch for user B leaves user A's client
 * names on screen indefinitely, and the skeleton stays hidden because the list is not empty.
 *
 * Covers the filtered list too (#5402). It is a second, disposable result set built from the same
 * rows, so leaving it behind would leak exactly what clearing `threads` was meant to prevent — and
 * it is what the page renders whenever a filter is on.
 *
 * Called from the auth store's `clearClientAuthData`, which runs for both sign-out and the
 * switching-account path, so one hook covers both.
 */
export function resetMessageInbox() {
    threads.value = [];
    loading.value = false;
    filteredThreads.value = [];
    filteredTotal.value = 0;
    searching.value = false;
}

// Module scope, and shared by both reads below: the unfiltered inbox and the filtered one come from
// the same query and so must produce the same shape. Two copies of this mapping would drift, and the
// filtered list feeds the same row rendering and the same `markThreadRead`.
const normalize = (row) => ({
    threadId: row.thread_id,
    participantRowId: row.participant_row_id,
    type: row.thread_type,
    status: row.thread_status,
    topic: row.thread_topic,
    // Comma-joined display names of the thread's OTHER active participants, so an untitled
    // thread can be named by who is in it instead of a generic label. Assembled in the query
    // because the inbox has no roster otherwise — `messaging-participant-read` lets a browser
    // read only its own participant row. No new exposure: the caller is a participant, and the
    // thread endpoint already hands them the full roster.
    otherParticipantNames: row.other_participant_names,
    // The client a conversation is about, and their name (#5332). Null on an internal thread.
    // A client conversation is titled by its client rather than by `otherParticipantNames`,
    // which for one of these would read as the OTHER clinician's name — or as nothing at all,
    // since the client is a Patient and that aggregate joins `practitioner`.
    //
    // Returning these at all is what the inbox query's new patient-access predicate pays for: the
    // row is only produced when the caller passes the chart-access rule for this client.
    subjectPatientId: row.subject_patient_id ?? null,
    subjectDisplayName: row.subject_display_name ?? null,
    authorId: row.author_id,
    lastMessageAt: row.last_message_at,
    lastReadAt: row.last_read_at,
    unread: row.unread === true,
});

export function useMessageInbox() {
    const fetchInbox = async ({ limit = 100, offset = 0 } = {}) => {
        loading.value = true;
        try {
            const qs = buildAidboxQueryString({ _limit: limit, _offset: offset });
            const result = await $httpFhirApi.get(`/$query/message-inbox?${qs}`);
            threads.value = extractAidboxQueryData(result).map(normalize);
        } catch (error) {
            // Degrade to the last good list rather than blanking the inbox on a transient failure.
            // Safe only because `resetMessageInbox` clears the list on sign-out and account switch —
            // the "last good list" can therefore only ever be this user's own, in this account.
            console.error('useMessageInbox: failed to load the inbox', error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Read the inbox narrowed by a search term and/or the status, type and unread filters (#5402).
     *
     * Server-side, and it has to be: the inbox is capped at 100 rows, so a filter applied in the
     * browser could only ever find a conversation that was already on the first page — which is the
     * complaint the ticket opens with. The query's own `{{user.practitioner.id}}` scoping is
     * untouched, so nothing here can widen what the caller may see; every parameter only narrows.
     *
     * Writes `filteredThreads`/`filteredTotal`, never `threads` — see the note on those refs for the
     * badge and read-receipt regressions that sharing one list would cause.
     *
     * @param {Object} [options]
     * @param {string} [options.q] free text matched against the title, participant names and client name
     * @param {string} [options.status] thread status, e.g. 'active' or 'closed'; '' for any
     * @param {string} [options.type] thread type, 'client' or 'internal'; '' for any
     * @param {boolean} [options.unreadOnly] only conversations with unread messages
     * @param {number} [options.limit] page size
     * @param {number} [options.offset] rows to skip
     */
    const fetchFilteredInbox = async ({ q = '', status = '', type = '', unreadOnly = false, limit = 100, offset = 0 } = {}) => {
        searching.value = true;
        try {
            // `undefined` for anything not being filtered on: `buildAidboxQueryString` drops those,
            // so the query falls back to its declared '' default and the predicate stays a
            // tautology. Sending an explicit empty string would work too, but omitting keeps the
            // request readable in the network log and identical to the unfiltered call's shape.
            const qs = buildAidboxQueryString({
                _limit: limit,
                _offset: offset,
                q: q || undefined,
                status: status || undefined,
                thread_type: type || undefined,
                unread_only: unreadOnly ? 'true' : undefined,
            });
            const result = await $httpFhirApi.get(`/$query/message-inbox?${qs}`);
            filteredThreads.value = extractAidboxQueryData(result).map(normalize);
            // The count-query's total, read defensively: where Aidbox puts it in an AidboxQuery
            // response is not something this code can rely on, and a paging control that shows a
            // wrong total is worse than one that shows the page it has. Falling back to the row
            // count means the pager degrades to "no further pages" rather than to nonsense.
            const total = result?.originalData?.total ?? result?.total;
            filteredTotal.value = Number.isFinite(Number(total)) && total !== null && total !== '' ? Number(total) : filteredThreads.value.length;
        } catch (error) {
            // Same degradation as `fetchInbox`: keep the last good result rather than blanking the
            // list, so a transient failure does not read as "nothing matched".
            console.error('useMessageInbox: failed to search the inbox', error);
        } finally {
            searching.value = false;
        }
    };

    /**
     * Mark a thread read by stamping lastReadAt on the caller's own participant row.
     *
     * The TRANSPORT is server-side as of #5376, and only the transport: marking read has to
     * ANNOUNCE, so a sender's read receipts update without a reload, and only the server can
     * publish to another user's realtime channel. The endpoint patches the caller's own row and
     * nothing else, exactly as the direct PATCH did — and the policy that permitted the direct
     * PATCH has since been deleted, because it could not constrain WHICH field was written.
     *
     * Everything around it is unchanged, deliberately: applied optimistically so the app-bar dot
     * clears immediately (#5331), rolled back if the request fails — a dot that wrongly persists
     * is a smaller problem than one that wrongly clears — and the state is module-scoped so the
     * badge and the list cannot disagree.
     *
     * Since #5402 the thread is looked for in BOTH lists, and every match is updated. A row opened
     * from a filtered list exists in `filteredThreads` and — because the unfiltered read is still
     * what the badge counts — usually in `threads` as well; they are separate objects from separate
     * responses, so updating only one would leave the other still showing unread. Still a no-op when
     * the thread is in neither.
     */
    const markThreadRead = async (threadId) => {
        const matches = [...threads.value, ...filteredThreads.value].filter((candidate) => candidate.threadId === threadId);
        if (!matches.length) return;

        const previous = matches.map((thread) => ({ thread, unread: thread.unread, lastReadAt: thread.lastReadAt }));
        const readAt = new Date().toISOString();

        matches.forEach((thread) => {
            thread.unread = false;
            thread.lastReadAt = readAt;
        });

        try {
            // The server's own timestamp wins over the optimistic one, so what the inbox holds is
            // what was actually stored.
            const result = await messagingApi.markThreadRead(threadId);
            if (result?.lastReadAt) matches.forEach((thread) => (thread.lastReadAt = result.lastReadAt));
        } catch (error) {
            previous.forEach(({ thread, unread, lastReadAt }) => {
                thread.unread = unread;
                thread.lastReadAt = lastReadAt;
            });
            console.error('useMessageInbox: failed to mark the thread read', error);
        }
    };

    return { threads, unreadCount, loading, fetchInbox, filteredThreads, filteredTotal, searching, fetchFilteredInbox, markThreadRead };
}

export default useMessageInbox;
